// ── Inline images ────────────────────────────────────────────────────────────
// Technical documentation constantly tells a reader to click an icon, and
// without an inline image the only ways to say which are to describe it ("the
// pencil icon"), quote its hover text, or give it a name ("click **Edit**").
// All three drift from the product, and the first two are unusable for an icon
// nobody recognises (ENG-57576).
//
// ── The representation, and why it is a URL fragment ───────────────────────
// An inline image is an ordinary markdown image whose URL carries `#inline`:
//
//     Click **Edit** ![Edit icon](https://…/assets/<id>.png#inline) to continue.
//
// It is markdown, so it is visible and editable in Raw mode and in the block
// editor, and it round-trips through every view untouched — which is what the
// ticket asks for. Three alternatives were considered and rejected:
//
//   • A `title` ("inline") — renders as a TOOLTIP a reader hovers and sees.
//   • A DOM rule — "an image in a paragraph that also has text is inline" needs
//     no marker at all, and is wrong on a real corpus pattern: a block image
//     followed by an italic caption is ONE paragraph with text in it, so every
//     existing figure would shrink to icon height. "Existing pages containing
//     block images are unaffected" is the criterion it fails.
//   • A custom `<Icon/>` element, like `<Pill/>` — not markdown, and the ticket
//     is explicit that the stored form is.
//
// A fragment is also the shape this corpus ALREADY uses for a hint that belongs
// to the page rather than to the file: `devhub-asset-rewrite.ts` treats `?` and
// `#` as URL boundaries precisely so `…/flow.png?width=400` keeps its sizing
// hint when the asset behind it is replaced. An inline marker rides the same
// rule for free — a replace preserves it, a delete cuts the whole construct.

/** The fragment that marks an image as inline. */
export const INLINE_IMAGE_HINT = "inline";

/** The class the renderer puts on one. Both repos use this exact string. */
export const INLINE_IMAGE_CLASS = "dh-inline-image";

/** Whether a URL asks for the inline treatment. Case-insensitive, because a
 *  writer editing Raw will type whatever they type. */
export function isInlineImageUrl(url: string): boolean {
  const hash = url.slice(url.indexOf("#") + 1);
  return url.includes("#") && hash.toLowerCase() === INLINE_IMAGE_HINT;
}

/**
 * The same URL, marked inline. Idempotent, and it replaces any existing
 * fragment rather than appending a second `#` — a URL with two is not a URL.
 * A query string is left alone: it belongs to the page too.
 */
export function inlineImageUrl(url: string): string {
  const base = url.split("#")[0];
  return `${base}#${INLINE_IMAGE_HINT}`;
}

/** The same URL without the marker — what "make this a block image again" is. */
export function blockImageUrl(url: string): string {
  return isInlineImageUrl(url) ? url.split("#")[0] : url;
}

/**
 * The markdown for an image.
 *
 * The ONE difference between an inline image and a block one, besides the
 * marker: the surrounding blank lines. A block image gets them so it lands as
 * its own paragraph; an inline one must not, or it would be lifted out of the
 * sentence it was inserted into — which is the whole point of the feature.
 */
export function imageMarkdownFor(url: string, alt: string, opts: { inline?: boolean } = {}): string {
  const text = `![${alt.trim().replace(/[[\]]/g, "")}](${opts.inline ? inlineImageUrl(url) : url})`;
  return opts.inline ? text : `\n\n${text}\n\n`;
}
